import { decodeAll } from './decoderPool';
import type { Frame, ManifestV2, PlaneId, Volume } from './types';

export interface LoadVolumeSource {
    /** URL of the manifest.json describing the volume. */
    manifestUrl: string;
    /** URL of the volume.bin. Falls back to the manifest's `volume.file`
     *  resolved relative to the manifest URL when omitted. */
    volumeUrl?: string;
    /** Optional bearer-token supplier for authenticated asset endpoints. */
    getAuthToken?: () => string | null;
}

/** Which stage of the load the volume is in. */
export type LoadPhase = 'manifest' | 'download' | 'decode';

/** Rich, real-time load status. `frac` is the overall 0..1 progress used to
 *  drive the bar; the per-phase fields let the UI show live byte / slice counts. */
export interface LoadProgress {
    phase: LoadPhase;
    frac: number;
    /** Bytes received / total (download phase). `totalBytes` is 0 when the
     *  server doesn't send Content-Length. */
    loadedBytes?: number;
    totalBytes?: number;
    /** Slices decoded / total (decode phase). */
    doneSlices?: number;
    totalSlices?: number;
}

// Split the progress bar between the two long-running stages.
const DOWNLOAD_SPAN = 0.5; // download occupies 0 .. 0.5
const DECODE_SPAN = 0.5; // decode occupies 0.5 .. 1

function authHeaders(getAuthToken?: () => string | null): HeadersInit | undefined {
    const token = getAuthToken?.() ?? null;
    return token ? { Authorization: `Bearer ${token}` } : undefined;
}

/** Download `url` into one contiguous Uint8Array, streaming the body so the
 *  caller sees real byte-level progress. Falls back to a plain buffered read
 *  when the response isn't streamable. */
async function downloadStreaming(
    res: Response,
    onProgress: (loaded: number, total: number) => void,
    signal?: AbortSignal
): Promise<Uint8Array> {
    const total = Number(res.headers.get('Content-Length')) || 0;
    if (signal?.aborted) throw new DOMException('load aborted', 'AbortError');

    // No readable stream (or older runtime): fall back to a single buffered read.
    if (!res.body || typeof res.body.getReader !== 'function') {
        const buf = new Uint8Array(await res.arrayBuffer());
        onProgress(buf.length, total || buf.length);
        return buf;
    }

    const reader = res.body.getReader();
    const chunks: Uint8Array[] = [];
    let received = 0;
    for (;;) {
        if (signal?.aborted) {
            await reader.cancel();
            throw new DOMException('load aborted', 'AbortError');
        }
        const { done, value } = await reader.read();
        if (done) break;
        if (!value) continue;
        chunks.push(value);
        received += value.length;
        onProgress(received, total);
    }

    const buf = new Uint8Array(received);
    let pos = 0;
    for (const c of chunks) {
        buf.set(c, pos);
        pos += c.length;
    }
    return buf;
}

/** Fetch the manifest + volume.bin and decode every slice into one Int16 volume.
 *  The download streams (real byte progress) and slices decode in parallel across
 *  a Web Worker pool (see `decoderPool.ts`); the main thread only copies each
 *  finished slice into the volume. */
export async function loadVolume(
    source: LoadVolumeSource,
    onProgress: (p: LoadProgress) => void,
    signal?: AbortSignal
): Promise<{ manifest: ManifestV2; volume: Volume }> {
    const headers = authHeaders(source.getAuthToken);

    onProgress({ phase: 'manifest', frac: 0 });
    const manifestRes = await fetch(source.manifestUrl, { headers, signal });
    if (!manifestRes.ok) throw new Error(`manifest fetch failed (${manifestRes.status})`);
    const manifest: ManifestV2 = await manifestRes.json();
    const { z: nz, y: ny, x: nx } = manifest.shape;
    const index = manifest.volume.index;

    // Validate the manifest BEFORE the ~460 MB allocation below: bad dims would
    // either overflow the buffer or allocate garbage, and a slice count that
    // disagrees with shape.z means the reslice math would read out of bounds.
    if (!(
        Number.isInteger(nz) &&
        Number.isInteger(ny) &&
        Number.isInteger(nx) &&
        nz > 0 &&
        ny > 0 &&
        nx > 0
    )) {
        throw new Error(`invalid volume shape (${nz}×${ny}×${nx})`);
    }
    if (index.length !== nz) {
        throw new Error(`manifest slice count (${index.length}) does not match shape.z (${nz})`);
    }
    // Data contract: the extractor pre-bakes Hounsfield units into the Int16
    // volume, so stored samples ARE HU and are used directly for window/level, mm
    // density and the ceph air=-1000 assumption. A non-identity rescale would mean
    // the stored samples are NOT HU; rather than silently mis-window every view,
    // fail loudly so the extractor contract is fixed upstream.
    const { slope, intercept } = manifest.volume.rescale ?? { slope: 1, intercept: 0 };
    if (slope !== 1 || intercept !== 0) {
        throw new Error(
            `volume.rescale must be identity (got slope=${slope}, intercept=${intercept}); ` +
                'the extractor must pre-bake HU into the Int16 volume'
        );
    }

    const volumeUrl =
        source.volumeUrl ?? new URL(manifest.volume.file, source.manifestUrl).toString();

    onProgress({ phase: 'download', frac: 0, loadedBytes: 0, totalBytes: 0 });
    const volRes = await fetch(volumeUrl, { headers, signal });
    if (!volRes.ok) throw new Error(`volume fetch failed (${volRes.status})`);

    const buf = await downloadStreaming(
        volRes,
        (loaded, total) => {
            // Clamp: gzip'd transports can report a compressed Content-Length that the
            // decompressed stream overshoots.
            const frac = total > 0 ? Math.min(loaded / total, 1) * DOWNLOAD_SPAN : 0;
            onProgress({ phase: 'download', frac, loadedBytes: loaded, totalBytes: total });
        },
        signal
    );

    const data = new Int16Array(nz * ny * nx);
    const sliceLen = ny * nx;
    const total = index.length;
    let done = 0;
    let lastPaint = 0;

    onProgress({ phase: 'decode', frac: DOWNLOAD_SPAN, doneSlices: 0, totalSlices: total });
    await decodeAll(
        buf,
        index,
        ({ z, pixels }) => {
            // Skip an out-of-range slice index so `data.set` can't write past the buffer.
            if (z < 0 || z >= nz) return;
            data.set(pixels.subarray(0, sliceLen), z * sliceLen);
            done++;
            if (done - lastPaint >= 16 || done === total) {
                lastPaint = done;
                onProgress({
                    phase: 'decode',
                    frac: DOWNLOAD_SPAN + DECODE_SPAN * (done / total),
                    doneSlices: done,
                    totalSlices: total
                });
            }
        },
        { signal }
    );

    return { manifest, volume: { data, nz, ny, nx } };
}

/**
 * Maps a display row/slice index to its z-index in the volume. Default
 * (`flipVertical` false) treats z=nz-1 as superior — the extractor's usual
 * convention. Some scans arrive with the opposite z convention (superior at
 * z=0), which renders every S-I-dependent view (axial stacking direction,
 * coronal/sagittal rows, panorama, cross-sections, cephalogram) upside down;
 * `flipVertical` swaps the mapping so the reader can correct those in place.
 */
export function displayZ(nz: number, i: number, flipVertical: boolean): number {
    return flipVertical ? i : nz - 1 - i;
}

/**
 * Reslice a 2D HU frame out of the volume:
 *  - axial(i):    z = displayZ(i)          → (nx × ny),  R left / A top (radiological)
 *  - coronal(j):  rows = z (displayZ)      → (nx × nz),  R left / S top
 *  - sagittal(k): rows = z (displayZ)      → (ny × nz),  P left / S top (y reversed)
 */
export function reslice(vol: Volume, plane: PlaneId, idx: number, flipVertical = false): Frame {
    const { data, nz, ny, nx } = vol;
    if (plane === 'axial') {
        const z = displayZ(nz, idx, flipVertical);
        const out = new Float32Array(nx * ny);
        const base = z * ny * nx;
        for (let i = 0; i < nx * ny; i++) out[i] = data[base + i];
        return { data: out, w: nx, h: ny };
    }
    if (plane === 'coronal') {
        const y = idx;
        const out = new Float32Array(nx * nz);
        for (let r = 0; r < nz; r++) {
            const z = displayZ(nz, r, flipVertical);
            const src = z * ny * nx + y * nx;
            const dst = r * nx;
            for (let x = 0; x < nx; x++) out[dst + x] = data[src + x];
        }
        return { data: out, w: nx, h: nz };
    }
    // sagittal
    const x = idx;
    const out = new Float32Array(ny * nz);
    for (let r = 0; r < nz; r++) {
        const z = displayZ(nz, r, flipVertical);
        const base = z * ny * nx + x;
        const dst = r * ny;
        // reverse y so posterior is on the left (A → right)
        for (let y = 0; y < ny; y++) out[dst + (ny - 1 - y)] = data[base + y * nx];
    }
    return { data: out, w: ny, h: nz };
}

/** In-plane bilinear sample of the volume at (x,y) on integer slice z. HU. */
export function sampleHU(vol: Volume, x: number, y: number, z: number): number {
    const { data, nz, ny, nx } = vol;
    if (x < 0) x = 0;
    else if (x > nx - 1) x = nx - 1;
    if (y < 0) y = 0;
    else if (y > ny - 1) y = ny - 1;
    if (z < 0) z = 0;
    else if (z > nz - 1) z = nz - 1;
    const x0 = x | 0,
        y0 = y | 0,
        z0 = z | 0;
    const x1 = x0 + 1 < nx ? x0 + 1 : x0;
    const y1 = y0 + 1 < ny ? y0 + 1 : y0;
    const fx = x - x0,
        fy = y - y0;
    const base = z0 * ny * nx;
    const v00 = data[base + y0 * nx + x0];
    const v10 = data[base + y0 * nx + x1];
    const v01 = data[base + y1 * nx + x0];
    const v11 = data[base + y1 * nx + x1];
    return v00 * (1 - fx) * (1 - fy) + v10 * fx * (1 - fy) + v01 * (1 - fx) * fy + v11 * fx * fy;
}
